import Swimlane from "../../../components/swimlane";
import React from "react";
import LicensedBook from "../book/licensed-book";
import { BASE_CARD_HEIGHT, BASE_CARD_WIDTH } from "../book";
import CategoryHead from "../../../components/categoryhead";
import styled from "styled-components";
import { EDGE_SPACING, EDGE_SPACING_MED } from "./consts";
import {
  styledMedia,
  withAllMatches
} from "../../../components/mediaquery/breakpoints";
import CenteredSpinner from "../../../components/spinner/centered-spinner";
import createScrollAction from "../../../components/swimlane/create-scroll-action";
import AccessibleSkipButton from "../../../components/accessible-skip-button";
import { t } from "../../../core/translate";

const ITEM_SPACING = 17;
const ITEM_SPACING_MED = 12;
const ITEM_SPACING_SM = 4;

const CARD_WIDTH_MED = 170;
const CARD_HEIGHT_MED = 244;
// only use regular size cards in grid, and reduce number of cards per row to fit
const CARD_WIDTH_SM = 170;
const CARD_HEIGHT_SM = 244;
export const HEADER_TYPES = {
  CLICKABLE: "clickable",
  UNCLICKABLE: "unclickable"
};

const Container = styled.div`
  margin-bottom: 20px;
`;

const HeadContainer = styled.h2`
  font-weight: 400;
  margin-bottom: 23px;
  /* 18px to accomodate Button padding alignment. */
  padding: 0 ${EDGE_SPACING}px 0 18px;
  ${styledMedia.medium`
    padding: 10px 0 0 0;
  `};
  mix-blend-mode: multiply;
`;

const LoadingBlock = styled.div`
  margin: 0 ${EDGE_SPACING}px 30px;
  height: ${BASE_CARD_HEIGHT}px;
  background-color: white;

  ${styledMedia.medium`
    margin: 0 ${EDGE_SPACING_MED}px 30px;
    height: ${CARD_HEIGHT_MED}px;
  `} ${styledMedia.small`
    height: ${CARD_HEIGHT_SM}px;
  `};
`;

const TitleCard = styled.li`
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
`;

function getStyles(isMedium, isSmall) {
  let itemSpacing = ITEM_SPACING;
  let edgeSpacing = EDGE_SPACING;

  if (isMedium) {
    itemSpacing = ITEM_SPACING_MED;
    edgeSpacing = EDGE_SPACING_MED;
  }

  if (isSmall) {
    itemSpacing = ITEM_SPACING_SM;
  }

  return {
    base: {
      marginLeft: itemSpacing,
      marginRight: itemSpacing
    },
    first: {
      marginLeft: edgeSpacing,
      marginRight: itemSpacing
    },
    last: {
      marginLeft: itemSpacing,
      marginRight: edgeSpacing
    }
  };
}

function BookSwimlane({
  isbns,
  BookObject = LicensedBook,
  onHeaderClick,
  headerType = "",
  headerLabel,
  count,
  titleCards = [],
  isMedium,
  isSmall,
  tryLoadMore = () => {},
  backArrow
}) {
  function getStyle(i) {
    const style = getStyles(isMedium, isSmall);

    if (i === 0) {
      return style.first;
    } else if (i === isbns.length - 1) {
      return style.last;
    }
    return style.base;
  }

  function renderBook({ i, inView }) {
    const isbnIndex = i - titleCards.filter(card => card.index <= i).length;
    const isbn = isbns[isbnIndex];

    let w = BASE_CARD_WIDTH;
    let h = BASE_CARD_HEIGHT;

    let titleCardToRender;
    titleCards.forEach(card => {
      // TODO if there are not enough books to fill up to this index, this could fail
      if (card.index === i) {
        titleCardToRender = (
          <TitleCard key={"TitleCard"} style={getStyle(i)}>
            <div style={{ width: w, height: "100%" }}>{card.content}</div>
          </TitleCard>
        );
      }
    });

    if (titleCardToRender) {
      return titleCardToRender;
    }

    return (
      <li key={isbn} style={getStyle(isbnIndex)}>
        <BookObject
          height={h}
          isbn={isbn}
          skeleton={!inView}
          underline={false}
          width={w}
          context={`swimlane-${headerLabel}`}
          index={i}
        />
      </li>
    );
  }

  function getAverageItemWidth() {
    if (isSmall) {
      return CARD_WIDTH_SM + ITEM_SPACING_SM * 2;
    }
    if (isMedium) {
      return CARD_WIDTH_MED + ITEM_SPACING_MED * 2;
    }
    return BASE_CARD_WIDTH + ITEM_SPACING * 2;
  }

  const handleScroll = createScrollAction(tryLoadMore);

  let skipAheadRef = null;
  let skipBackRef = null;

  function skipAheadClick() {
    skipAheadRef.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    //Focus added here with preventScroll to actually focus on element
    skipAheadRef.focus({ preventScroll: true });
  }

  function skipBackClick() {
    skipBackRef.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    //Focus added here with preventScroll to actually focus on element
    skipBackRef.focus({ preventScroll: true });
  }

  if (count === 0) {
    return null;
  }

  let clickableHeader = false;
  if (count > 5) {
    clickableHeader = true;
  }
  if (headerType === HEADER_TYPES.UNCLICKABLE) {
    clickableHeader = false;
  }
  if (headerType === HEADER_TYPES.CLICKABLE) {
    clickableHeader = true;
  }
  return (
    <Container>
      <HeadContainer>
        <CategoryHead
          count={count}
          hideCount
          onClick={clickableHeader ? onHeaderClick : null}
          label={headerLabel}
          caretRight={clickableHeader}
          backArrow={backArrow}
          skipAhead={count >= 1}
          skipAheadClick={skipAheadClick}
          focusRef={el => {
            skipBackRef = el;
          }}
        />
      </HeadContainer>
      {isbns.length > 0 ? (
        <Swimlane
          renderItem={renderBook}
          averageItemWidth={getAverageItemWidth()}
          itemCount={isbns.length + titleCards.length}
          onScroll={handleScroll}
        />
      ) : (
        <LoadingBlock>
          <CenteredSpinner />
        </LoadingBlock>
      )}

      <AccessibleSkipButton
        focusRef={el => {
          skipAheadRef = el;
        }}
        skipClick={skipBackClick}
        label={t("base.skip-back", "Skip Back")}
        top={-25}
        left={35}
      />
    </Container>
  );
}


export default withAllMatches(BookSwimlane);
